import { Component } from 'react';
import '../../../../styles/components/quiz-spinner.scss';
import { Gift, Loader, Maximize2, Minimize2, PartyPopper, RotateCw, Sparkles } from 'lucide-react';
import { Spin } from './services/ramadanQuizServices';
import { toast } from 'react-toastify';

// ── SVG Ramadan Particle definitions ──────────────────────────────────────────
// Three shapes cycle: crescent moon, 4-pointed star, 8-pointed Islamic star
const PARTICLE_SVGS = [
  // Crescent moon
  (key, style) => (
    <svg
      key={key}
      className="ramadan-particle"
      style={style}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill="currentColor" />
    </svg>
  ),
  // 4-pointed star / diamond star
  (key, style) => (
    <svg
      key={key}
      className="ramadan-particle"
      style={style}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2L13.8 9.2L21 12L13.8 14.8L12 22L10.2 14.8L3 12L10.2 9.2L12 2Z"
        fill="currentColor"
      />
    </svg>
  ),
  // 8-pointed Islamic / Rub el Hizb star
  (key, style) => (
    <svg
      key={key}
      className="ramadan-particle"
      style={style}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 1L14.39 7.61L21 5L18.39 11.61L25 12L18.39 12.39L21 19L14.39 16.39L12 23L9.61 16.39L3 19L5.61 12.39L-1 12L5.61 11.61L3 5L9.61 7.61L12 1Z"
        fill="currentColor"
        transform="scale(0.92) translate(1.04 1.04)"
      />
    </svg>
  ),
];

// Pre-generate deterministic positions to avoid layout shift on re-render
const PARTICLE_COUNT = 28;
const particles = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
  id: i,
  svgIndex: i % 3,
  left: `${(i * 37.3 + 7) % 100}%`,
  delay: `${(i * 0.73) % 8}s`,
  duration: `${7 + ((i * 1.13) % 7)}s`,
  size: `${10 + ((i * 3.7) % 14)}px`,
  opacity: 0.45 + (i % 3) * 0.15,
}));

class QuizSpinner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      digit1: 0,
      digit2: 0,
      digit3: 0,
      isSpinning: false,
      winnerNumber: null,
      prize: null,
      showWinnerModal: false,
      remaining: null,
      showCompletionPage: false,
      isFullscreen: false,
      isCompleted: false,
    };
    this.spinInterval = null;
    this.stopTimeout = null;
  }

  componentDidMount() {
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);
  }

  componentWillUnmount() {
    if (this.spinInterval) clearInterval(this.spinInterval);
    if (this.stopTimeout) clearTimeout(this.stopTimeout);
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
  }

  startSpin = async () => {
    if (this.state.isSpinning) return;
    this.setState({ isSpinning: true, showWinnerModal: false, winnerNumber: null });

    this.spinInterval = setInterval(() => {
      this.setState({
        digit1: Math.floor(Math.random() * 10),
        digit2: Math.floor(Math.random() * 10),
        digit3: Math.floor(Math.random() * 10),
      });
    }, 50);

    this.stopTimeout = setTimeout(async () => {
      try {
        const response = await Spin();
        console.log(response, 'l');

        if (!response.success) {
          toast.error(response.message);
          clearInterval(this.spinInterval);
          this.setState({ isSpinning: false, showCompletionPage: response.is_completed ?? true });
          return;
        }
        this.stopSpinning(response.data, response.is_completed);
      } catch (error) {
        console.error('Error calling spin API:', error);
        clearInterval(this.spinInterval);
        this.setState({ isSpinning: false });
        toast.error('Something went wrong. Please try again.');
      }
    }, 2000);
  };

  stopSpinning = (data, isCompleted = false) => {
    clearInterval(this.spinInterval);
    const winnerStr = data.number.toString().padStart(3, '0');
    this.setState({
      digit1: parseInt(winnerStr[0]),
      digit2: parseInt(winnerStr[1]),
      digit3: parseInt(winnerStr[2]),
      winnerNumber: data.number,
      prize: data.prize_name,
      remaining: data.remaining ?? 0, // ← now safely read from data
      isCompleted,
    });
    setTimeout(() => {
      this.setState({ isSpinning: false, showWinnerModal: true });
    }, 500);
  };

  closeModal = () => {
    const { isCompleted } = this.state;
    if (isCompleted) {
      this.setState({ showWinnerModal: false, showCompletionPage: true });
    } else {
      this.setState({ showWinnerModal: false });
    }
  };

  handleFullscreenChange = () => {
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
    this.setState({ isFullscreen: isCurrentlyFullscreen });
  };

  toggleFullscreen = () => {
    const elem = document.documentElement;
    if (this.state.isFullscreen) {
      (
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen
      )?.call(document);
    } else {
      (
        elem.requestFullscreen ||
        elem.webkitRequestFullscreen ||
        elem.mozRequestFullScreen ||
        elem.msRequestFullscreen
      )?.call(elem);
    }
  };

  render() {
    const {
      digit1,
      digit2,
      digit3,
      isSpinning,
      showWinnerModal,
      winnerNumber,
      showCompletionPage,
      isFullscreen,
      prize,
    } = this.state;

    if (showCompletionPage) {
      return (
        <div className="completion-page">
          <div className="completion-backdrop"></div>
          <div className="completion-glow-orb completion-glow-orb-1"></div>
          <div className="completion-glow-orb completion-glow-orb-2"></div>
          <div className="completion-content">
            <div className="completion-shine"></div>
            <div className="completion-icon-wrapper">
              <div className="completion-icon">
                <PartyPopper size={80} strokeWidth={1.5} />
              </div>
              <div className="completion-icon-ring"></div>
            </div>
            <h1 className="completion-title">
              <span className="completion-title-text">السحب المحظوظ</span>
              <span className="completion-title-text">اكتمل!</span>
            </h1>
            <div className="completion-divider">
              <Sparkles size={20} />
            </div>
            <p className="completion-message">
              شكرًا لمشاركتك في مسابقة رمضان. نسأل الله أن يجلب لك هذا الشهر المبارك الفرح والسلام
              والكثير من الأجر.
            </p>
            <div className="completion-footer">
              <p className="completion-tagline">رمضان كريم – نراكم في التحدي القادم!</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="lucky-draw-container">
        {/* Fullscreen Button */}
        <button
          className="fullscreen-button"
          onClick={this.toggleFullscreen}
          aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        >
          {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
        </button>

        {/* Background overlay */}
        <div className="bg-gradient-overlay"></div>

        {/* ── Ramadan SVG Floating Particles ── */}
        <div className="ramadan-particles" aria-hidden="true">
          {particles.map((p) =>
            PARTICLE_SVGS[p.svgIndex](p.id, {
              left: p.left,
              width: p.size,
              height: p.size,
              animationDelay: p.delay,
              animationDuration: p.duration,
              '--particle-opacity': p.opacity,
            })
          )}
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Logo */}
          <div className="logo-top-center">
            <img
              src={process.env.PUBLIC_URL + '/assets/images/ramadan-khalil.png'}
              alt="Ramadan Al-Khalil Logo"
              className="top-logo-img"
            />
          </div>

          {/* Spinner Section */}
          <div className="spinner-section">
            <div className="spinner-wrapper">
              <div className="spinner-glow"></div>
              <div className="spinner-container">
                <div className="spinner-frame">
                  {/* Top arch ornament */}
                  <div className="frame-arch-top">
                    <svg viewBox="0 0 200 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M0 20 Q50 4 100 2 Q150 4 200 20"
                        stroke="currentColor"
                        strokeWidth="1"
                        fill="none"
                        opacity="0.6"
                      />

                      {/* Small stars along arch */}
                      {/* <path
                        d="M60 10 L61.2 13.6 L65 13.6 L62.2 15.8 L63.4 19.4 L60 17.2 L56.6 19.4 L57.8 15.8 L55 13.6 L58.8 13.6 Z"
                        fill="currentColor"
                        opacity="0.5"
                      />
                      <path
                        d="M140 10 L141.2 13.6 L145 13.6 L142.2 15.8 L143.4 19.4 L140 17.2 L136.6 19.4 L137.8 15.8 L135 13.6 L138.8 13.6 Z"
                        fill="currentColor"
                        opacity="0.5"
                      /> */}
                    </svg>
                  </div>

                  {/* Decorative corners */}
                  <div className="frame-corner frame-corner-tl"></div>
                  <div className="frame-corner frame-corner-tr"></div>
                  <div className="frame-corner frame-corner-bl"></div>
                  <div className="frame-corner frame-corner-br"></div>

                  {/* Left decorations */}
                  <div className="frame-decoration frame-left">
                    {/* <div className="deco-bolt"></div>
                    <div className="deco-bolt"></div>
                    <div className="deco-bolt"></div> */}
                  </div>

                  {/* Digit Display */}
                  <div className="spinner-display">
                    <div className={`digit-box ${isSpinning ? 'spinning' : ''}`}>
                      <div className="digit-inner">
                        <div className="digit digit-gold">{digit1}</div>
                      </div>
                    </div>
                    <div className="digit-separator">
                      <svg viewBox="0 0 12 40" fill="none">
                        <circle cx="6" cy="10" r="2.5" fill="currentColor" opacity="0.4" />
                        <circle cx="6" cy="20" r="2.5" fill="currentColor" opacity="0.6" />
                        <circle cx="6" cy="30" r="2.5" fill="currentColor" opacity="0.4" />
                      </svg>
                    </div>
                    <div className={`digit-box ${isSpinning ? 'spinning' : ''}`}>
                      <div className="digit-inner">
                        <div className="digit">{digit2}</div>
                      </div>
                    </div>
                    <div className="digit-separator">
                      <svg viewBox="0 0 12 40" fill="none">
                        <circle cx="6" cy="10" r="2.5" fill="currentColor" opacity="0.4" />
                        <circle cx="6" cy="20" r="2.5" fill="currentColor" opacity="0.6" />
                        <circle cx="6" cy="30" r="2.5" fill="currentColor" opacity="0.4" />
                      </svg>
                    </div>
                    <div className={`digit-box ${isSpinning ? 'spinning' : ''}`}>
                      <div className="digit-inner">
                        <div className="digit">{digit3}</div>
                      </div>
                    </div>
                  </div>

                  {/* Right decorations */}
                  <div className="frame-decoration frame-right">
                    {/* <div className="deco-bolt"></div>
                    <div className="deco-bolt"></div>
                    <div className="deco-bolt"></div> */}
                  </div>

                  {/* Bottom ornament */}
                  <div className="frame-bottom-ornament">
                    <svg viewBox="0 0 160 12" fill="none">
                      <line
                        x1="0"
                        y1="6"
                        x2="60"
                        y2="6"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        opacity="0.3"
                      />
                      <path
                        d="M75 6 L77.5 1 L80 6 L82.5 11 L85 6"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        fill="none"
                        opacity="0.6"
                      />
                      <line
                        x1="100"
                        y1="6"
                        x2="160"
                        y2="6"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        opacity="0.3"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Spin Button */}
            <button
              className={`spin-button ${isSpinning ? 'spinning' : ''}`}
              onClick={this.startSpin}
              disabled={isSpinning}
            >
              <span className="button-shine"></span>
              <span className="button-content">
                <RotateCw className="button-icon" strokeWidth={2.5} />
                <span className="button-text">ابدأ الدوران الآن</span>
              </span>
            </button>

            <p className="status-text">
              {isSpinning ? (
                <>
                  <span className="status-icon">
                    <Loader className="animate-spin" />
                  </span>
                  جاري الدوران ...
                </>
              ) : (
                <>
                  <span className="status-icon"></span>في انتظار الفائز التالي
                </>
              )}
            </p>
          </div>
        </div>

        {/* Winner Modal */}
        {showWinnerModal && (
          <div className="modal-overlay-l2" onClick={this.closeModal}>
            <div className="confetti-wrapper">
              {[...Array(150)].map((_, i) => (
                <div
                  key={i}
                  className="confetti-piece"
                  style={{
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 3}s`,
                    backgroundColor:
                      i % 4 === 0
                        ? '#C9A84C'
                        : i % 4 === 1
                          ? '#F0D898'
                          : i % 4 === 2
                            ? '#8B1A2E'
                            : '#E4C47A',
                    animationDuration: `${2 + Math.random() * 2}s`,
                  }}
                ></div>
              ))}
            </div>

            <div className="winner-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-bg-glow"></div>
              <div className="modal-shine"></div>

              <div className="celebration-badge">
                <PartyPopper size={30} strokeWidth={1.5} />
              </div>

              <h2 className="modal-title">
                <span className="modal-title-line">تهانينا !</span>
              </h2>

              <div className="winner-display">
                <div className="winner-display-glow"></div>
                <div className="winner-label">رقم الجائزة</div>
                <div className="winner-number">{winnerNumber?.toString().padStart(3, '0')}</div>
              </div>

              {prize && (
                <div className="prize-banner">
                  <div className="prize-banner-shine"></div>
                  <div className="prize-label">تم فتح الجائزة</div>
                  <div className="prize-bottom">
                    <div className="prize-icon">
                      <Gift className="prize-gift-icon" strokeWidth={2.5} />
                    </div>
                    <div className="prize-name">
                      {prize}{' '}
                      {(prize.includes('ريال') || prize.includes('نقدية')) && (
                        <span className="prize-omr-badge">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 798.31 440.47"
                            className="prize-omr-svg"
                            aria-label="Omani Rial"
                          >
                            <path d="M81.06,299.73l45.71-82.79,156.27-.37c-1.56-58.06,20.14-128.93,58.17-173.5,46.34-54.3,115.4-27.63,161.54,11.98,5.82,4.99,22.82,20.21,22.58,26.93l-30.86,117.94c-36.47-40.56-83.3-85.47-143.16-75.47-11.27,1.88-26.48,12.6-32.28,22.36-14.43,24.25,15.49,53.68,31.72,69.75h437.78l-46.1,83.16h-309.95c13.29,11.36,32.13,21.87,48.28,28.94,8.46,3.7,40.8,16.2,47.95,16.2h188.69l-46.1,83.16H9.77l46.33-83.16h291.08l-33.27-45.15H81.06Z" />
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="thank-you-card">
                <p className="thank-you-text">شكرًا لمشاركتك !</p>
              </div>

              <button className="modal-button" onClick={this.closeModal}>
                <span className="modal-button-glow"></span>
                <span className="modal-button-text">متابعة</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default QuizSpinner;
